import { z } from 'zod';

import { currencySchema } from './currency';
import { isoDateTimeSchema, minorUnitsBigintSchema, ulidSchema, userIdSchema } from './primitives';

export const payoutRecordStatusSchema = z.enum(['CREATED', 'IN_PROGRESS', 'COMPLETED', 'FAILED']);
export type PayoutRecordStatus = z.infer<typeof payoutRecordStatusSchema>;

export const payoutTriggeredBySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('SCHEDULER') }),
  z.object({
    kind: z.literal('ADMIN'),
    userId: userIdSchema,
    reason: z.string().min(10).max(1000),
    bypassMinimum: z.boolean(),
    waiveServiceFee: z.boolean(),
    force: z.boolean(),
  }),
]);
export type PayoutTriggeredBy = z.infer<typeof payoutTriggeredBySchema>;

export const payoutBreakdownEntrySchema = z.object({
  orderId: ulidSchema,
  subtotalMinorUnits: minorUnitsBigintSchema,
  refundedFromSubtotalMinorUnits: minorUnitsBigintSchema,
  netSubtotalMinorUnits: minorUnitsBigintSchema,
  percentageFeeMinorUnits: minorUnitsBigintSchema,
  deliveryFeeForRestaurantMinorUnits: minorUnitsBigintSchema,
});
export type PayoutBreakdownEntry = z.infer<typeof payoutBreakdownEntrySchema>;

export const payoutEntitySchema = z.object({
  payoutId: ulidSchema,
  restaurantId: ulidSchema,
  currency: currencySchema,
  restaurantRevenueMinorUnits: minorUnitsBigintSchema,
  percentageFeeMinorUnits: minorUnitsBigintSchema,
  payoutServiceFeeChargedMinorUnits: minorUnitsBigintSchema,
  amountToPayoutMinorUnits: minorUnitsBigintSchema,
  serviceFeeBreakdown: z.array(payoutBreakdownEntrySchema),
  orderIds: z.array(ulidSchema),
  status: payoutRecordStatusSchema,
  triggeredBy: payoutTriggeredBySchema,
  createdAt: isoDateTimeSchema,
  completedAt: isoDateTimeSchema.optional(),
});
export type PayoutEntity = z.infer<typeof payoutEntitySchema>;

export const payoutOwnerSchema = z.object({
  payoutId: ulidSchema,
  restaurantId: ulidSchema,
  currency: currencySchema,
  restaurantRevenueMinorUnits: z.string(),
  percentageFeeMinorUnits: z.string(),
  payoutServiceFeeChargedMinorUnits: z.string(),
  amountToPayoutMinorUnits: z.string(),
  serviceFeeBreakdown: z.array(
    z.object({
      orderId: ulidSchema,
      subtotalMinorUnits: z.string(),
      netSubtotalMinorUnits: z.string(),
      percentageFeeMinorUnits: z.string(),
      deliveryFeeForRestaurantMinorUnits: z.string(),
    }),
  ),
  orderIds: z.array(ulidSchema),
  status: payoutRecordStatusSchema,
  triggeredByKind: z.enum(['SCHEDULER', 'ADMIN']),
  createdAt: isoDateTimeSchema,
  completedAt: isoDateTimeSchema.optional(),
});
export type PayoutOwner = z.infer<typeof payoutOwnerSchema>;

export const payoutAdminSchema = payoutOwnerSchema.extend({
  triggeredBy: payoutTriggeredBySchema,
});
export type PayoutAdmin = z.infer<typeof payoutAdminSchema>;
